import type { DocsContent } from "./types";

const cn = {
  locale: "cn",
  shell: {
    navigationLabel: "文档导航",
    browse: "浏览文档",
    title: "Comfort 指南",
    description: "依次查看原则和基础。",
    readDesign: "阅读 DESIGN.md",
    paginationLabel: "上一篇与下一篇文档",
    previous: "上一篇",
    next: "下一篇",
    reviewed: "最近检查 · 2026年8月",
    outlineLabel: "页面目录",
    outlineTitle: "本页内容",
    overviewGroup: "开始使用",
    sections: { principles: "原则", foundations: "基础" },
  },
  search: {
    ariaLabel: "筛选列表",
    label: "按名称查找",
    placeholder: "输入名称或用途",
    reset: "重置搜索",
    result: "项结果",
    results: "项结果",
  },
  principles: {
    eyebrow: "设计原则",
    title: "打造舒适产品的四项准则",
    description:
      "Comfort追求的不只是柔和的外观。用户应该能看懂下一步、预期操作结果，并在不改变熟悉用法的前提下逐步发现更多功能。",
    outlineModel: "整体了解",
    outlineReview: "发布前检查",
    intent: "为什么需要",
    practice: "如何应用",
    modelLabel: "四项Comfort原则组成的连续流程",
    modelCenter: "清晰带来信任",
    reviewBadge: "发布前检查",
    reviewTitle: "发布新模式前请确认这些事项",
    reviewItems: [
      "无需只依赖颜色，也能从文案、位置和状态看懂下一步操作。",
      "与相似组件保持一致，并使用semantic token而非临时例外。",
      "反馈出现在操作附近，并给出清晰的继续或返回路径。",
      "无需改变熟悉的用法，也能逐步展示进阶功能。",
    ],
    items: [
      {
        id: "natural",
        number: "01",
        title: "自然",
        comfortTitle: "无需解释也能看懂下一步",
        summary: "使用人们已经熟悉的语言和动作，减少思考与操作负担。",
        practice:
          "使用简单文案，保持空间关系，把主要操作放在视线自然停留的位置。",
        question: "不看说明也知道下一步该做什么吗？",
      },
      {
        id: "certain",
        number: "02",
        title: "可预期",
        comfortTitle: "相同情况使用相同方式回应",
        summary: "稳定的规则和必要的选项能让用户放心操作，不必反复猜测。",
        practice: "使用semantic token，让相似组件具备一致的状态与反馈。",
        question: "操作之前能预期结果吗？",
      },
      {
        id: "meaningful",
        number: "03",
        title: "有目的",
        comfortTitle: "让注意力回到重要任务",
        summary:
          "视觉和交互应帮助用户看懂目的、确认进度，并在遇到问题时找到返回路径。",
        practice:
          "突出核心任务，在操作附近提供反馈，减少干扰下一步判断的装饰。",
        question: "它能帮助完成任务或理解当前状态吗？",
      },
      {
        id: "growing",
        number: "04",
        title: "可成长",
        comfortTitle: "初次使用简单，熟悉后逐步深入",
        summary:
          "第一次使用就能发挥作用，随着使用逐步发现更多功能，并能平稳扩展。",
        practice:
          "逐步展示高级功能，保留已经学会的用法，把反馈用于下一次改进。",
        question: "功能增加后还能沿用已经学会的操作吗？",
      },
    ],
  },
  foundations: {
    eyebrow: "基础",
    title: "集中管理各个界面反复使用的准则",
    description:
      "颜色、字体、间距和动效会在产品中重复出现。先确定它们的作用，再按任务需要进行组合。",
    outlineCatalog: "查找基础",
    outlineDirectory: "完整列表",
    outlineLayers: "应用顺序",
    featuredNote: "示例与用法",
    emptyTitle: "没有找到相关基础",
    emptyDescription: "请尝试更短的名称或更宽泛的用途。",
    directoryEyebrow: "15项基础",
    directoryTitle: "完整列表",
    layersBadge: "应用顺序",
    layersTitle: "从意图到实际界面",
    layersDescription: "前一步确定的准则会让下一步更容易选择。",
    layers: [
      ["01", "原则", "说明界面为什么采用这种行为"],
      ["02", "Semantic token", "定义切换主题后仍然稳定的角色"],
      ["03", "组件", "把角色变成可以重复使用的交互"],
    ],
  },
  foundationDetail: {
    eyebrow: "基础",
    overview: "预览",
    intent: "用途",
    guidelines: "应用方法",
    accessibility: "无障碍",
    reference: "参考值",
    intentEyebrow: "为什么需要这项准则",
    intentTitle: "看看它让哪些体验保持一致",
    guidanceBadge: "核心指南",
    guidanceTitle: "应用到产品时需要检查什么",
    accessibilityEyebrow: "让每个人都能使用",
    accessibilityTitle: "无障碍检查",
    referenceTitle: "参考值",
    referenceDescription:
      "角色名称保持稳定，实际值可以根据主题、视口、语言和交互状态调整。",
    role: "角色",
    use: "用途",
    value: "参考值",
    sourceTitle: "在 DESIGN.md 中继续了解",
    sourceDescription:
      "产品实际使用的值和检查项由DESIGN.md管理，本页用于直观查看结果。",
    overviewDescription: "基础概览",
  },
  notFound: {
    title: "找不到这个页面",
    description: "返回首页，从原则或基础中继续查找。",
    action: "返回Comfort首页",
  },
  foundationCopy: {
    "design-token": {
      description: "一套语义化词汇，让决策在不同主题和产品之间保持可迁移。",
      intent: "先描述用途再决定外观，组件就能在不改变含义的前提下适应变化。",
      rules: [
        "按意图为角色命名。",
        "别名统一经由一个来源映射。",
        "让浅色与深色角色保持平行。",
      ],
      uses: ["操作与焦点", "页面底色", "内容表面"],
      accessibility: ["令牌变化时保持对比度。", "状态角色不只依赖颜色。"],
    },
    color: {
      description: "冷静的中性色构成安静的结构，一种蓝色标识交互。",
      intent: "在不把颜色变成装饰的前提下，厘清层级、操作和状态。",
      rules: [
        "蓝色只用于交互。",
        "保留语义化的状态颜色。",
        "在两种主题下都要验证。",
      ],
      uses: ["浅色主题的操作", "深色主题的操作", "正文内容"],
      accessibility: ["正文文字达到4.5比1的对比度。", "非文字控件达到3比1。"],
    },
    typography: {
      description: "为产品内容、辅助文字和代码准备的紧凑Geist字阶。",
      intent: "在添加底面之前，先用字号、字重、行长和间距传达层级。",
      rules: [
        "使用句式大小写。",
        "标题保持平衡换行，正文自然换行。",
        "等宽字体只用于功能性数据。",
      ],
      uses: ["主视觉陈述", "产品文案", "元数据"],
      accessibility: [
        "放大到200%时文字仍能重新排布。",
        "中日韩文字保持语义分组。",
      ],
    },
    iconography: {
      description: "一致的几何图标辅助扫读，而不取代文字标签。",
      intent: "维持同一套视觉语法，图标不熟悉时让文字承载含义。",
      rules: ["保持描边粗细一致。", "使用视觉对齐。", "避免陈旧的隐喻。"],
      uses: ["行内辅助", "标准控件", "插图用途"],
      accessibility: [
        "装饰性图标对辅助技术隐藏。",
        "纯图标控件要有可访问名称。",
      ],
    },
    elevation: {
      description: "表面对比与克制的阴影解释真实的堆叠关系。",
      intent: "呈现包含与层叠，而不让每个区域都浮起来。",
      rules: [
        "先用色调，再用阴影。",
        "阴影与堆叠顺序保持一致。",
        "光源方向只保留一个。",
      ],
      uses: ["页面内容", "卡片与栏", "菜单与浮层", "对话框"],
      accessibility: ["选中状态不只依赖高度。", "高对比度下边界依然清晰。"],
    },
    gradient: {
      description: "受控的渐变只服务于数据、遮罩和既定的主视觉文字。",
      intent: "只有当连续性本身传达信息时才使用连续的颜色。",
      rules: [
        "界面背景保持平整。",
        "为数据渐变添加标注。",
        "避免装饰性的AI渐变。",
      ],
      uses: ["有序的量级", "边缘的延续", "标题强调"],
      accessibility: ["渐变两端满足对比度要求。", "数据另有非颜色的表达方式。"],
    },
    "inclusive-design": {
      description: "语义、对比、焦点和宽容的交互塑造每一个组件。",
      intent: "让核心任务在不同的感知、输入、缩放和动效偏好下都可以完成。",
      rules: [
        "优先使用原生语义。",
        "让焦点始终可见。",
        "在失败旁边设计恢复路径。",
      ],
      uses: ["触控目标", "可见的焦点环", "最小宽度"],
      accessibility: ["以WCAG 2.2 AA为基线。", "辅助技术遵循同样的任务顺序。"],
    },
    "international-design": {
      description: "布局和内容经得起翻译、书写方向和区域格式的变化。",
      intent: "把语言膨胀和方向切换当作正常的产品状态，而不是例外。",
      rules: [
        "为文案预留30%的膨胀空间。",
        "使用逻辑属性。",
        "按区域设置格式化日期和数字。",
      ],
      uses: ["文案余量", "布局逻辑", "格式表达"],
      accessibility: ["语言与方向属性设置正确。", "翻译后的标签保持完整。"],
    },
    layout: {
      description: "响应式容器和稳定的阅读顺序守住任务的优先级。",
      intent: "调整密度和栏数时，不改变含义，也不让关键操作不可预测地移动。",
      rules: ["从源顺序开始。", "多轴布局使用网格。", "限制适合阅读的行长。"],
      uses: ["紧凑页面", "阅读行长", "最大外壳"],
      accessibility: ["320像素宽度下页面不溢出。", "缩放后任务顺序保持不变。"],
    },
    motion: {
      description: "原因、方向和连续性让状态变化更容易理解。",
      intent: "解释发生了什么变化，同时不拖延输入，也不添加装饰性的位移。",
      rules: [
        "在100毫秒内开始反馈。",
        "只对transform和opacity做动画。",
        "为减少动态效果提供同等信息。",
      ],
      uses: ["按压反馈", "状态变化", "面板移动"],
      accessibility: ["减少动态效果时移除位移。", "没有动画也能看到状态。"],
    },
    radius: {
      description: "克制的圆角比例让容器柔和而精确。",
      intent: "表达包含与嵌套，而不把每个控件都变成胶囊形。",
      rules: [
        "大表面内部使用更小的圆角。",
        "胶囊形只留给状态标识。",
        "接缝处保持直角。",
      ],
      uses: ["标签与片段", "控件", "卡片", "对话框"],
      accessibility: ["形状不是唯一的状态线索。", "焦点环沿可见轮廓绘制。"],
    },
    spacing: {
      description: "四像素的间距比例区分决策，并把相关内容聚在一起。",
      intent: "在添加边框、卡片或装饰性填充之前，先用节奏建立层级。",
      rules: [
        "只使用文档化的间距档位。",
        "同级元素之间优先用gap而不是margin。",
        "分区节奏按视觉效果微调。",
      ],
      uses: ["紧密的标签", "控件内部", "相关内容", "大区块分隔"],
      accessibility: ["触控目标之间保持足够间距。", "文字变长时间距不塌陷。"],
    },
    state: {
      description: "共享的生命周期区分等待、可用、空白和可恢复的结果。",
      intent: "呈现正在发生什么、哪些内容仍然安全、哪个操作可以恢复任务。",
      rules: [
        "刷新时保留旧数据。",
        "骨架屏只用于几何形状已知的区域。",
        "错误信息放在恢复手段旁边。",
      ],
      uses: ["没有可用数据", "保留旧数据", "可以恢复"],
      accessibility: ["状态变化会被恰当播报。", "焦点不会在无意间跳转。"],
    },
    "voice-and-tone": {
      description: "直接而平静的语言，按风险和语境调整强调程度。",
      intent: "帮助人们做决定并恢复任务，不含糊其辞，也不指责。",
      rules: ["先说结果。", "点明对象和后果。", "紧迫感与真实风险相匹配。"],
      uses: ["日常引导", "确认的结果", "恢复引导"],
      accessibility: ["避免使用习语。", "紧急文案不靠标点技巧也能理解。"],
    },
    writing: {
      description: "具体的界面文案直接点明操作、结果、状态和恢复路径。",
      intent: "用平实的主动语态写出下一个决定，减少理解成本。",
      rules: [
        "使用主动语态。",
        "操作名称包含动词和结果。",
        "错误恢复就地说明。",
      ],
      uses: ["操作标签", "恢复文案", "下一步操作"],
      accessibility: ["标签脱离界面语境也能理解。", "翻译时不拼接句子碎片。"],
    },
  },
} satisfies DocsContent;

export default cn;
